'use client'

/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from 'react';
import dynamic from 'next/dynamic';
import { ComponentNames as WnCComponentNames } from 'lib/constant/component-constant';
import MainCanvasProps from './WnCMainCanvasProps';
import MainCanvasState from './WnCMainCanvasState';

/**---------------------------------------------------------------------------------
 * Core Components -- Load always ------------ */
const DtvFirstName = dynamic(() => import("@directv-digital/dds.ui.dtv-firstname"));
import { useStoryblokConfigs } from '@directv-digital/dds.server.application-context';
import DtvBox from '@directv-digital/dds.ui.dtv-box';
import DtvVerticalSpacer from '@directv-digital/dds.ui.dtv-vertical-spacer';
// dynamic (SSR stays on) so these leave the entry chunk: DtvHero isn't rendered
// on every page, and the others are below-the-fold on most CMS stories.
const DtvPageSectionHeader = dynamic(() => import('@directv-digital/dds.ui.dtv-page-section-header'));
const DtvInPageLegal = dynamic(() => import('@directv-digital/dds.ui.dtv-in-page-legal'));
//import ContentCards from '@directv-digital/dds.ui.dtv-content-cards';
const ContentCards = dynamic(() => import('@/video-lab/dtv-content-cards'));
const DtvHero = dynamic(() => import('@directv-digital/dds.ui.dtv-hero'));
// WAT-10279: MarketingAI modal now controlled by page-load-modal orchestrator
// import MarketingAiModal from "@/video-lab/marketing-ai-modal";
const ErrorDisplay = dynamic(() => import("@directv-digital/dds.ui.error-display"), {ssr: false});
// WAT-10279: Revisitor modal now controlled by page-load-modal orchestrator
// const DtvRevisitorModal = dynamic(() => import('@/video-lab/dtv-revisitor-modal/dtv-revisitor-modal').then(mod => mod.DtvRevisitorModal), {ssr: false});

/**---------------------------------------------------------------------------------
 * Channel/GP Tools (maintain as relative imports, publish to VL as needed) ------------- */
const ChnlListingPage = dynamic(
  () => import("src/components/ChnlListingPageV2"));
const DtvAgentPackageCompareTool = dynamic(
      () => import("src/components/dtv-agent-package-compare-tool"));
const ChlupInteractiveToolV2 = dynamic(
  () => import("src/components/ChlupInteractiveToolV2"));
const LocalRsnTool = dynamic(
  () => import("src/components/video-lab/local-rsn-tool"));
const RsnLookupTool = dynamic(
  () => import("src/components/video-lab/rsn-lookup-tool"));
const RsnFeeLookup = dynamic(
  () => import("src/components/video-lab/rsn-fee-lookup"));
const LocalChannelsLookupPage = dynamic(
  () => import("src/components/LocalChannelsLookupPage"));
const PackageCarousel = dynamic(
  () => import("@/video-lab/package-carousel/package-carousel"));
const FlipCards = dynamic(
  () => import("@/video-lab/flip-cards").then(mod => ({ default: mod.FlipCards })),
);

/**---------------------------------------------------------------------------------
 * Components (maintain as a video lab import, unless otherwise needed) ------------
 * Please try to keep me alphabetized :) ------------------------------------------- */
const AmbientVideoMarquee = dynamic(
  () => import("@/video-lab/ambient-video-marquee"));
const ChannelLogoCarousal = dynamic(
  () => import("@directv-digital/dds.ui.channel-logo-carousal"));
const DishPointer = dynamic( // Note: UF WnC Canvas only (No StreamTV)
  () => import("@directv-digital/dds.ui.dtv-dish-pointer"));
const Dtv4UpIconList = dynamic(
  () => import("@directv-digital/dds.ui.dtv-four-up-icon-list"));
const DtvAgnosticPlanCards = dynamic(
  () => import('@directv-digital/dds.ui.dtv-agnostic-plan-cards'));
const DtvAppDownloadBanner = dynamic(
  () => import('@/video-lab/dtv-app-download-banner'));
const DtvBreadCrumbs = dynamic(
  () => import("@directv-digital/dds.ui.dtv-breadcrumbs"));
const DtvCompetitorTableModule = dynamic(
  () => import("@/video-lab/competitor-table"));
const DtvCountdown = dynamic(
  () => import("@directv-digital/dds.ui.dtv-countdown"));
const DtvEmailCapture = dynamic(
  () => import("@directv-digital/dds.ui.dtv-email-capture"));
const DtvEmailModal = dynamic(
  () => import("@directv-digital/dds.ui.dtv-email-capture-popup-modal"));
const DtvExitModal = dynamic(
  () => import("src/components/video-lab/dtv-exit-modal"), {ssr: false});
const DtvExternalScriptLoader = dynamic(
  () => import("@directv-digital/dds.ui.dtv-external-script-loader"));
const DtvExternalServiceComparison = dynamic(
  () => import("@directv-digital/dds.ui.dtv-external-service-comparison-chart"));
const DtvExternalServiceComparisonChartTabs = dynamic(
  () => import("@/video-lab/dtv-external-service-comparison-chart-tabs"));
const DtvFaq = dynamic(
  () => import('@directv-digital/dds.ui.dtv-faq'));
const DtvFormBuilder = dynamic(
  () => import("@directv-digital/dds.ui.dtv-form-builder"));
const DtvFindRetailer = dynamic(
  () => import("@directv-digital/dds.ui.dtv-find-retailer"));
const DtvFloatingwidget = dynamic(
  () => import("@directv-digital/dds.ui.dtv-floating-widget-v3"), { ssr: false });
const DtvHeadLine = dynamic(
  () => import("@directv-digital/dds.ui.dtv-headline"));
const DtvInsider = dynamic(
  () => import("@directv-digital/dds.ui.dtv-insider"));
const DtvInternalProductComparisionChart = dynamic(
  () => import("@directv-digital/dds.ui.dtv-internal-product-comparison-chart"));
const DtvLinkForest = dynamic(
  () => import("@directv-digital/dds.ui.link-forest"));
const DtvListItem = dynamic(
  () => import("@directv-digital/dds.ui.dtv-list"));
const DtvLongFormatCard = dynamic(
  () => import('@directv-digital/dds.ui.dtv-longformat-card'));
const DtvMduHero = dynamic(
  () => import("@/video-lab/dtv-mdu-hero"));
const DtvOfferCards = dynamic(
  () => import("@/video-lab/dtv-offer-cards/dtv-offer-cards").then(mod => mod.DtvOfferCards));
const DtvQuizComponent = dynamic(
  () => import("@directv-digital/dds.ui.dtv-quiz-component"));
const DtvThreeUp = dynamic(
  () => import("@directv-digital/dds.ui.dtv-three-up"));
const EquipmentWizard = dynamic(
  () => import("@/video-lab/equipment-wizard"));
const featuredArticleList = dynamic(
  () => import("@/video-lab/dtv-featured-article-list"));
const ImageMarquee = dynamic(
  () => import("@directv-digital/dds.ui.image-marquee"));
const InPageNav = dynamic(
  () => import("@directv-digital/dds.ui.inpagenav"));
const MarqueeCarousel = dynamic(
  () => import("@directv-digital/dds.ui.marquee-carousel"));
const MediaModule = dynamic(
  () => import("@directv-digital/dds.ui.dtv-media-module"));
const MessagingBanner = dynamic(
  () => import("@directv-digital/dds.ui.dtv-messaging-banner"));
const MessagingCard = dynamic(
  () => import("@directv-digital/dds.ui.dtv-messaging-card"));
const PackageComparisonSavingsCalculator = dynamic(
  () => import("@directv-digital/dds.ui.package-comparison-calculator"));
const PlanPortal = dynamic(
  () => import('@directv-digital/dds.ui.plan-portal'));
const ReviewCarousel = dynamic(
  () => import("src/components/video-lab/review-carousel"));
const RotatingMarque = dynamic(
  () => import("@directv-digital/dds.ui.rotating-marque"));
const DtvRowCanvas = dynamic(
  () => import("@directv-digital/dds.ui.dtv-row-canvas"));
const ScrollToTop = dynamic(
  () => import("@directv-digital/dds.ui.scroll-to-top"));
const SearchComponent = dynamic(
  () => import("@directv-digital/dds.ui.search-component"));
const SearchWatchNow = dynamic(
  () => import("@directv-digital/dds.ui.search-guide"));
const SeoContent = dynamic(
  () => import("@directv-digital/dds.ui.seo-content"));
const SiteMap = dynamic(
  () =>import ('@/video-lab/dtv-sitemap'));
const ShowCarousal = dynamic(
  () => import("src/components/video-lab/show-carousal"));
const TvPromise = dynamic(
  () => import("@directv-digital/dds.ui.tv-promise"));
const UcgGalleryLoader = dynamic(
  () => import("@directv-digital/dds.ui.ucg-gallery-loader"));
const VerticalVideoCarousel = dynamic(
  () => import("@directv-digital/dds.ui.vertical-video-carousel"));
const VideoMarquee = dynamic(
  () => import("@directv-digital/dds.ui.video-marquee"));
const videoCarousel = dynamic(
  () => import("@directv-digital/dds.ui.video-carousel"));
const VideoMarqueeCampaign = dynamic(
  () => import('@directv-digital/dds.ui.video-marquee-campaign'));
const VideoPopUpMarquee = dynamic(
  () => import("@directv-digital/dds.ui.video-popup-marquee"));
const YotpoPackageReview = dynamic(
  () => import("@/video-lab/yotpo-package-review"));
const YotpoPackageRating = dynamic(
  () => import("@/video-lab/yotpo-package-rating"));
const DtvMarquee = dynamic(()=> import("@directv-digital/dds.ui.dtv-marquee"));

/**
 * Fusion components
 */
const ContentCardC = dynamic(
  () => import('@directv-digital/fusion.organisms.content-card-c'));

/**---------------------------------------------------------------------------------
 * Components (maintain as relative imports, publish to VL as needed) ------------- */
const ConspiracyBoard = dynamic(
  () => import("src/components/video-lab/conspiracy-board"));
const DtvPlanCardModule = dynamic(
  () => import('src/components/video-lab/plan-cards/dtv-plan-card-v2'));
const DtvHorizontalPlanCard = dynamic(
  () => import("src/components/video-lab/plan-cards/dtv-horizontal-plan-card"));
const DtvTogglePlanCard = dynamic(
  () => import("src/components/video-lab/plan-cards/dtv-toggle-plan-card"));
const Iframe = dynamic(
  () => import("src/components/iframe"));
const FlappyPigeon = dynamic(
  () => import('src/components/flappy-pigeon/components/flappy-pigeon'));
const PackageCompareTool = dynamic(
  () => import("@/video-lab/package-compare-tool"));
const ChannelPackLineup = dynamic(
  () => import("@/video-lab/channel-pack-lineup"));
const InPageChannelPackLineup = dynamic(
    () => import("@/video-lab/inpage-channel-pack-lineup"));
const GenrePreviewTool = dynamic(
  () => import("@/video-lab/genre-preview-tool"));
const AiProductRecommender = dynamic(
  () => import("@/video-lab/ai-product-recommender"));
const MediaCardImage1Up2UpV2 = dynamic(
  () => import("@/video-lab/media-module-v2/media-module-image"));
const GenrePackDetailsPanel = dynamic(
    () => import("@/video-lab/genre-pack-details-panel/genre-pack-details-panel"));
  const DtvCompetitorComparisonTable = dynamic(
    () => import("@/video-lab/dtv-competitor-comparison-table"));
const MarketingBanner = dynamic(
  () => import("@directv-digital/dds.ui.marketing-banner"));
const ChannelCompareToolV3 = dynamic(
  () => import("@/video-lab/channel-compare-tool-v3"));
const CustomerAcqCompareTool = dynamic(
  () => import("src/components/custom-acq-compare-tool"));

/**
 * Registry mapping each blok type to its React component (mostly next/dynamic
 * loadables — never raw React.lazy; the spec pins that invariant with a
 * source-level scan of this file, so no export is needed for it).
 */
const WnCComponentMap: { [key: string]: any } = {
  [WnCComponentNames.INPAGENAV]: InPageNav,
  [WnCComponentNames.DTVMARQUEE]:DtvMarquee,
  [WnCComponentNames.DTVCAROUSEL]: MarqueeCarousel,
  [WnCComponentNames.ROTATINGMARQUEE]: RotatingMarque,
  [WnCComponentNames.VIDEOMARQUEE]: VideoMarquee,
  [WnCComponentNames.IMAGEMARQUEE]: ImageMarquee,
  [WnCComponentNames.VIDEOPOPUPMARQUEE]: VideoPopUpMarquee,
  [WnCComponentNames.VIDEOCAROUSEL]: videoCarousel,
  [WnCComponentNames.DISHPOINTER]: DishPointer,
  [WnCComponentNames.LONGFORMATCARD]: DtvLongFormatCard,
  [WnCComponentNames.CONTENTCARDS]: ContentCards,
  [WnCComponentNames.PAGESECTIONHEADER]: DtvPageSectionHeader,
  [WnCComponentNames.PLANCARD]: DtvPlanCardModule,
  [WnCComponentNames.HORIZONTAL_PLAN_CARD]: DtvHorizontalPlanCard,
  [WnCComponentNames.TOGGLE_PLAN_CARD]: DtvTogglePlanCard,
  [WnCComponentNames.FAQ]: DtvFaq,
  [WnCComponentNames.INPAGELEGAL]: DtvInPageLegal,
  [WnCComponentNames.BREADCRUMBSV2]: DtvBreadCrumbs,
  [WnCComponentNames.INTERNALPRODCUTCOMPARISIONCHART]: DtvInternalProductComparisionChart,
  [WnCComponentNames.DTVHERO]: DtvHero,
  [WnCComponentNames.VERTICALSPACER]: DtvVerticalSpacer,
  [WnCComponentNames.HEADLINE]: DtvHeadLine,
  [WnCComponentNames.LIST_ITEM]: DtvListItem,
  [WnCComponentNames.FOUR_UP_ICON_LIST]: Dtv4UpIconList,
  [WnCComponentNames.MESSAGINGBANNER]: MessagingBanner,
  [WnCComponentNames.MESSAGINGCARD]: MessagingCard,
  [WnCComponentNames.EXTERNALSERVICECOMPARISON]: DtvExternalServiceComparison,
  [WnCComponentNames.EXTERNALSERVICECOMPARISONTABS]: DtvExternalServiceComparisonChartTabs,
  [WnCComponentNames.DTV1UP2UPMEDIA]: MediaModule,
  [WnCComponentNames.DTVAGNOSTICPLANCARDS]: DtvAgnosticPlanCards,
  [WnCComponentNames.DTVEXTERNALSCRIPTLOADER]: DtvExternalScriptLoader,
  [WnCComponentNames.DTVMDUHERO]: DtvMduHero,
  [WnCComponentNames.DTVYOTPOGALLERYWRAPPER]: UcgGalleryLoader,
  [WnCComponentNames.FLOATINGWIDGETV2]: DtvFloatingwidget,
  [WnCComponentNames.FORM_BUILDERV2]: DtvFormBuilder,
  [WnCComponentNames.QUIZV2]: DtvQuizComponent,
  [WnCComponentNames.EMAILPOPUPMODAL]: DtvEmailModal,
  [WnCComponentNames.EMAILCAPTUREV2]: DtvEmailCapture,
  [WnCComponentNames.COMPETITORTABLE] : DtvCompetitorTableModule,
  [WnCComponentNames.DTVIFRAME]: Iframe,
  [WnCComponentNames.ROWCANVASV2]: DtvRowCanvas,
  [WnCComponentNames.CHANNEL_COMPARISON_TOOL_V2]: ChannelCompareToolV3,
  [WnCComponentNames.CHANNEL_LISTING_PAGE] : ChnlListingPage,
  [WnCComponentNames.DTVAGENTPACKAGECOMPARETOOL] : DtvAgentPackageCompareTool,
  [WnCComponentNames.SATELLITE_CHANNEL_LISTING_PAGE] : ChnlListingPage,
  [WnCComponentNames.INSIDERARTICLE]: DtvInsider,
  [WnCComponentNames.SITEMAP] : SiteMap,
  [WnCComponentNames.CHANNELLOGOCAROUSAL] : ChannelLogoCarousal,
  [WnCComponentNames.DTVFIRSTNAME] : DtvFirstName,
  [WnCComponentNames.DTVTHREEUP] : DtvThreeUp,
  [WnCComponentNames.LINKFOREST] : DtvLinkForest,
  [WnCComponentNames.CHLUP_INTERACTIVE_TOOL] : ChlupInteractiveToolV2,
  [WnCComponentNames.PACKAGECOMPARISONCALCULATOR]: PackageComparisonSavingsCalculator,
  [WnCComponentNames.LOCALCHANNELSLOOKUPPAGE]: LocalChannelsLookupPage,
  // [WnCComponentNames.REFERENCECOMPONENT]: ReferenceComponent,
  [WnCComponentNames.FINDRETAILER]: DtvFindRetailer,
  [WnCComponentNames.SEARCHCOMPONENT]:SearchComponent,
  [WnCComponentNames.MARKETINGBANNER]: MarketingBanner,
  [WnCComponentNames.SHOWCAROUSAL] : ShowCarousal,
  [WnCComponentNames.OFFERCARDS] : DtvOfferCards,
  [WnCComponentNames.SEARCHWATCHNOW] : SearchWatchNow,
  [WnCComponentNames.SEOCONTENT] : SeoContent,
  [WnCComponentNames.DTVCOUNTDOWN] : DtvCountdown,
  [WnCComponentNames.TVPROMISE] : TvPromise,
  [WnCComponentNames.EXITMODAL]: DtvExitModal,
  // WAT-10279: MarketingAI modal now controlled by page-load-modal orchestrator
  // [WnCComponentNames.MARKETINGAIMODAL]: MarketingAiModal,
  [WnCComponentNames.VIDEOMARQUEECAMPAIGN]: VideoMarqueeCampaign,
  [WnCComponentNames.FLAPPYPIGEON] : FlappyPigeon,
  [WnCComponentNames.SCROLLTOTOP]: ScrollToTop,
  [WnCComponentNames.RSNLOOKUPTOOL]: RsnLookupTool,
  [WnCComponentNames.RSNFEELOOKUP]: RsnFeeLookup,
  [WnCComponentNames.REVIEWCAROUSEL]: ReviewCarousel,
  [WnCComponentNames.APPDOWNLOADBANNER]: DtvAppDownloadBanner,
  [WnCComponentNames.VERTICALVIDEOCAROUSEL] : VerticalVideoCarousel,
  [WnCComponentNames.LOCALRSNTOOL]: LocalRsnTool,
  [WnCComponentNames.YOTPOPACKAGEREVIEWWIDGET]: YotpoPackageReview,
  [WnCComponentNames.PLANPORTAL]: PlanPortal,
  [WnCComponentNames.CONSPIRACYBOARD]: ConspiracyBoard,
  [WnCComponentNames.PACKAGECOMPARETOOL]: PackageCompareTool,
  [WnCComponentNames.CHANNELPACKLINEUP] : ChannelPackLineup,
  [WnCComponentNames.INPAGECHANNELPACKLINEUP] : InPageChannelPackLineup,
  [WnCComponentNames.GENREPREVIEWTOOL]: GenrePreviewTool,
  [WnCComponentNames.AIPRODUCTRECOMMENDER]: AiProductRecommender,
  [WnCComponentNames.EQUIPMENTWIZARD]: EquipmentWizard,
  [WnCComponentNames.FEATUREDARTICLELIST]: featuredArticleList,
  [WnCComponentNames.AMBIENTVIDEOMARQUEE]: AmbientVideoMarquee,
  [WnCComponentNames.YOTPOPACKAGERATING]: YotpoPackageRating,
  [WnCComponentNames.MEDIA_CARD_IMAGE_1UP_2UP_V2]: MediaCardImage1Up2UpV2,
  // WAT-10279: Revisitor modal now controlled by page-load-modal orchestrator
  // [WnCComponentNames.REVISITORMODAL]: DtvRevisitorModal,
  [WnCComponentNames.PACKAGECAROUSEL]: PackageCarousel,
  [WnCComponentNames.FLIPCARDS]: FlipCards,
  [WnCComponentNames.GENREPACKDETAILSPANEL]: GenrePackDetailsPanel,
  [WnCComponentNames.COMPETITORCOMPARISONTABLEV2] : DtvCompetitorComparisonTable,
  [WnCComponentNames.CONTENTCARDC]: ContentCardC,
  [WnCComponentNames.CUSTOMERACQCOMPARETOOL]: CustomerAcqCompareTool,
};

const aboveTheFoldCount = 2;

/**
 * Section types exempt from the contentVisibility:'auto' section style below:
 * they read scroll/layout or render fixed-position UI, so their paint must not
 * be containment-gated. (FLOATINGWIDGETV2 is scroll-reactive too but the style
 * special-cases it to 'inherit' instead of exempting it.) The eager-hydration
 * set below derives from this list so the superset relationship is structural,
 * not a comment.
 */
export const CONTENT_VISIBILITY_EXEMPT: ReadonlySet<WnCComponentNames> = new Set<WnCComponentNames>([
  WnCComponentNames.SCROLLTOTOP,   // renders fixed-position UI (scroll button)
  WnCComponentNames.RSNLOOKUPTOOL, // owns anchor/scroll behavior (layout reader)
  WnCComponentNames.RSNFEELOOKUP,  // owns anchor/scroll behavior (layout reader)
  WnCComponentNames.PLANCARD,      // renders position:fixed children (see its exclusion spec)
]);
/**
 * Bloks that fire GA4 impressions from MOUNT effects (not user interaction).
 * Deferred hydration would shift impression timing/order — and a
 * pre-hydration bounce would drop the event entirely (undercount) — so this
 * whole list is spread into HYDRATION_EAGER_TYPES below (structural, like the
 * CONTENT_VISIBILITY_EXEMPT superset).
 *
 * Census discipline: this list cannot discover firers by itself. When wiring
 * a NEW WnCComponentMap entry, grep its import chain for
 * fireGa4ProductImpression / view_item_list / fireGa4ItemList inside a mount
 * effect and add the type here; the spec's census backstop pins listed
 * members but not unlisted ones. dtv-flash is a known UNWIRED firer today —
 * it must join this list the moment it gets a map entry.
 *
 * Checked and deliberately NOT listed: DTVCAROUSEL / PACKAGECAROUSEL (no
 * mount impressions — the package carousel's mount IntersectionObserver only
 * gates auto-scroll), SvodBannerV2 (fires from a click handler, which
 * selective hydration replays).
 */
export const MOUNT_IMPRESSION_TYPES: ReadonlySet<WnCComponentNames> = new Set<WnCComponentNames>([
  WnCComponentNames.PLANCARD, // also paint-exempt above; fires at idle from mount
  WnCComponentNames.CUSTOMERACQCOMPARETOOL,
  WnCComponentNames.CHANNEL_COMPARISON_TOOL,    // inert today: bare type has no map entry, so only the channels=internet remap to _V2 renders; kept defensively
  WnCComponentNames.CHANNEL_COMPARISON_TOOL_V2, // the real firer; served by channel-compare-tool-v3 (map entry above)
  WnCComponentNames.OFFERCARDS,
  WnCComponentNames.GENREPREVIEWTOOL,
  WnCComponentNames.HORIZONTAL_PLAN_CARD,           // mount rIC view_item_list, same shape as PLANCARD
  WnCComponentNames.CHANNEL_LISTING_PAGE,           // ChnlListingPage fires on-load impressions at idle
  WnCComponentNames.SATELLITE_CHANNEL_LISTING_PAGE, // same module as CHANNEL_LISTING_PAGE
  // Transitive: renders DtvHorizontalPlanCardModule (orientation==='horizontal')
  // else DtvPlanCardV2Module; both fire view_item_list from a mount rIC effect,
  // so the wrapper type itself must stay eager even though it never fires directly.
  WnCComponentNames.TOGGLE_PLAN_CARD,
  WnCComponentNames.DTVAGNOSTICPLANCARDS, // dtv-agnostic-plan-cards: fireGa4ProductImpression in a mount useEffect
  WnCComponentNames.DTV1UP2UPMEDIA,       // dtv-media-module: fireGa4ProductImpression in a mount useEffect
]);
/**
 * Section types kept on the eager hydration path: everything paint-exempt
 * above (scroll/layout readers must also init their JS before any tap —
 * scrolling never targets a boundary, so React would not prioritize them),
 * every mount-impression firer (census above), plus members eager for their
 * own reasons, annotated inline.
 * NOTE: the v2 (App Router) canvas has no defer path yet. When it grows one,
 * extract this set to a shared module so both canvases consume one source of
 * truth (follow-up WAT sub-task under WAT-11303 tracks this).
 * Entries MUST be WnCComponentNames enum references, never raw key strings —
 * the enum has value≠key members (e.g. EXITMODAL = "EXITMODALV2") where a raw
 * key string would silently never match component.type; the Set's element type
 * makes a raw string a compile error.
 */
export const HYDRATION_EAGER_TYPES: ReadonlySet<WnCComponentNames> = new Set<WnCComponentNames>([
  ...CONTENT_VISIBILITY_EXEMPT,
  ...MOUNT_IMPRESSION_TYPES,
  WnCComponentNames.FLOATINGWIDGETV2,        // scroll-reactive; style-handled via 'inherit'
  WnCComponentNames.VERTICALSPACER,          // hydration cost ~0; wrapping adds bookkeeping only
  WnCComponentNames.EXITMODAL,               // ssr:false — server renders nothing to defer; keeps exit-intent capture timing
  WnCComponentNames.DTVEXTERNALSCRIPTLOADER, // pixel/tag injection timing must not change
  WnCComponentNames.BREADCRUMBSV2,           // slot-free in the fold counter; text-only nav, keep rules consistent
]);
/**
 * Types excluded from the nonSpacerCount fold counter (the third rule that
 * used to live as a raw-string chain): spacers/widgets/breadcrumbs consume no
 * visual above-the-fold slot, so they must not push real content past the
 * eager window. Kept separate from the sets above — counter exclusion,
 * paint exemption, and eager hydration are different rules that only
 * partially overlap.
 * (Exported for the spec's derived eager-type table, which iterates
 * HYDRATION_EAGER_TYPES minus these slot-free members.)
 */
export const COUNTER_EXCLUDED: ReadonlySet<WnCComponentNames> = new Set<WnCComponentNames>([
  WnCComponentNames.VERTICALSPACER,
  WnCComponentNames.FLOATINGWIDGETV2,
  WnCComponentNames.BREADCRUMBSV2,
]);
/** Internal class — receives sbPreviewAllowed as a prop instead of reading context */
export class WnCMainCanvas extends React.Component<MainCanvasProps & { sbPreviewAllowed?: boolean }, MainCanvasState> {

  constructor(props: MainCanvasProps & { sbPreviewAllowed?: boolean }) {
    super(props);
    this.buildAndUpdateState();
  }

  private buildAndUpdateState = () => {
    const content = this.buildPageComponents(this.props.data) || [];
    this.state = {
      contentLow: content[1],
      contentTop: content[0],
    };
  }

  render(): JSX.Element {
    if(this.props.sbPreviewAllowed){
      this.buildAndUpdateState();
    }

    const state: MainCanvasState = this.state;

    return (
      <>
        {state.contentTop}
        {state.contentLow}
      </>
    );
  }
  private setRecommendationProp(newComponentProps) {
    return {
      ...newComponentProps,
    };
  }
  private buildPageComponents = (data: any): JSX.Element[] => {
    if (!data || !Array.isArray(data)) {
      data = [];
    }
    let returnComponents: JSX.Element[] = [];
    let nonSpacerCount = -1;
    const { props } = this;

    //TO DO : check for supress component && !suppressComponentContext[this?.props?.pagePath].includes(component?.props?.options?.id)
    // const suppressComponentContext = this.context?.globalConfigs?.SuppressComponentList;

    // Effective-type + prop-unwrap helpers for the section-header suppression guard below.
    // Hoisted above the map so they are allocated once per render, not per block (matches the
    // App-router twin's module-scope resolveComponentType/extractComponentProps).
    const resolveBlockType = (block: any) => (block?.type === "OVERWRITER" ? block?.props?.component : block?.type);
    const unwrapProps = (block: any) => {
      if (block?.type !== "OVERWRITER") {
        return block?.props;
      }
      const objProps = block?.props?.objProps;
      return Array.isArray(objProps) ? objProps?.[0] : objProps;
    };

    const builtWnCComponents: JSX.Element[] = data.map((component: any, index: number) => {
      try {
        // Suppress the standalone section header that immediately precedes a parallax Show Carousel:
        // the parallax variant renders its own title, so a title-only PAGESECTIONHEADER above it is a
        // duplicate. Both blocks may arrive OVERWRITER-wrapped (type "OVERWRITER", real type under
        // props.component), so resolve the effective type the same way the render path does below.
        if (resolveBlockType(component) === "PAGESECTIONHEADER") {
          // Skip layout-only spacers so an interleaved VERTICALSPACER doesn't defeat the guard.
          let lookahead = index + 1;
          while (data?.[lookahead] && resolveBlockType(data[lookahead]) === "VERTICALSPACER") {
            lookahead += 1;
          }
          const nextBlock = data?.[lookahead];
          const nextVariant =
            nextBlock?.props?.variant ||
            nextBlock?.props?.objProps?.variant ||
            nextBlock?.props?.objProps?.[0]?.variant;
          // The parallax child renders its heading only when the carousel carries one
          // (`{headingText && ...}` — toText() -> "" when absent). Only drop the standalone
          // header when the carousel actually has a title to replace it with. The parallax
          // carousel authors its title in the top-level `header` (same source the parallax render
          // falls back to), so key off that alone — a carousels[] shape simply won't suppress.
          const nextHeaderSrc = unwrapProps(nextBlock);
          const nextHeader = nextHeaderSrc?.header;
          const nextHeaderNode = Array.isArray(nextHeader) ? nextHeader?.[0] : nextHeader;
          const nextHasOwnTitle =
            typeof nextHeaderNode === "string"
              ? !!nextHeaderNode
              : !!(nextHeaderNode?.content || nextHeaderNode?.title);
          // The carousel only replaces the TITLE. Never drop a header that also carries an eyebrow,
          // body copy, legal disclosure, a CTA, an image, a sticker or icon text — the parallax
          // variant renders none of those (fields per DtvPageSectionHeaderProps).
          const headerProps = unwrapProps(component);
          const headerIsTitleOnly = !(
            headerProps?.eyebrow?.content ||
            headerProps?.body?.content ||
            headerProps?.legal ||
            headerProps?.cta?.label ||
            headerProps?.cta?.imageUrl ||
            headerProps?.optionalCta?.label ||
            headerProps?.optionalCta?.imageUrl ||
            headerProps?.image?.imageUrl ||
            headerProps?.image?.tabletImgUrl ||
            headerProps?.image?.mobileImgUrl ||
            headerProps?.sticker ||
            headerProps?.iconText ||
            headerProps?.iconImageUrl
          );
          if (
            resolveBlockType(nextBlock) === "SHOWCAROUSAL" &&
            nextVariant === "parallax" &&
            nextHasOwnTitle &&
            headerIsTitleOnly
          ) {
            return null;
          }
        }
        if (component && component.type) {
          const isOverwriter = component.type === "OVERWRITER";
          // One binding for "which component actually renders", consumed by
          // the map lookup here AND the defer/paint verdicts below.
          // Previously the CHANNEL_COMPARISON_TOOL->V2 query-param
          // remap lived only in the map lookup, so the verdicts were correct
          // solely because both variants are eager — an unenforced cross-list
          // invariant. Deriving everything from one binding removes the
          // invariant instead of documenting it.
          // (The old CHANNEL_LINEUP_FFP myFreeDtv branch resolved to the SAME
          // lookup as the plain fallthrough — enum value IS the literal key,
          // so both are WnCComponentMap['CHANNEL_LINEUP_FFP'], which has no
          // entry today (the branch was already inert; FFP renders nothing in
          // this canvas). Folded away, not dropped.)
          const effectiveComponentType = (
            isOverwriter
              ? component?.props?.component :
            component.type === "CHANNEL_COMPARISON_TOOL" && props?.reqQueryParams?.channels == "internet"
              ? WnCComponentNames.CHANNEL_COMPARISON_TOOL_V2 :
              component.type
          ) as WnCComponentNames;
          const comp = WnCComponentMap[effectiveComponentType];
          if (comp) {
            const WnCComponent = comp;

            // BREADCRUMBSV2 carries no image (text nav only) so it can never be the LCP,
            // but it would otherwise consume an above-the-fold slot and push the real hero
            // (e.g. DtvMarquee) out of the eager-load window -> lazy LCP. Exclude it from the count.
            // (Legacy "BREADCRUMBS" needs no exclusion: it has no WnCComponentMap entry, so the
            // `if (comp)` guard above skips it entirely and it never reaches this increment.)
            // Note: BREADCRUMBSV2 still receives aboveTheFold/lazyLoadImages for its slot below,
            // but DtvBreadCrumbs ignores those image-loading props, so the passed values are inert.
            // The counter reads the LITERAL type on purpose: an OVERWRITER
            // remapping to a slot-free type still advances the count
            // (inherited pre-PR behavior; flipping it to the remapped type
            // would shift the fold on existing OVERWRITER pages — out of
            // scope here.
            if (!COUNTER_EXCLUDED.has(component.type as WnCComponentNames)) {
              nonSpacerCount++;
            }

            const componentProps: any = (
              isOverwriter
                ? (component?.props?.objProps || component?.props?.objProps?.[0])
                : component.props
            );

            let newComponentProps: any = {};
            if (componentProps) {
              newComponentProps = {
                ...componentProps,
                ...{
                  options: {
                    ...componentProps.options,
                    ...{ isAuthenticatedUser: props.options.isAuthenticatedUser },
                    recognizedUserData: props.options.recognizedUserData,
                    isQuizv2 :  component.type === "QUIZV2"
                  },
                  enableConfigureCart: props?.options?.enableConfigureCart
                }
              };
            }

            const spacer = {
              afterHero: {
                mobile: "100px 0px",
                medium: "120px 0px",
              },
              afterPlanCards: {
                mobile: "100px 0px",
                medium: "200px 0px",
              },
              betweenContentCards: {
                mobile: "20px 0px",
                medium: "20px 0px",
              },
            };

            const uniqueIdForVerticalSpacer =
              newComponentProps?.type === "VERTICALSPACER" ? `dtv2-VerticalSpacer${index}` :
              `dtv2-${newComponentProps?.options?.id}`;
            const uniqueDataSectionForVerticalSpacer =
              newComponentProps?.type === "VERTICALSPACER" ? `VerticalSpacer${index}` :
              `${newComponentProps?.options?.id}`;

            // Defer hydration of below-the-fold sections (INP audit FP-06: the whole
            // blok tree hydrated as ONE long task that early taps inherit as input
            // delay). SEO invariant: Suspense children render synchronously on the
            // server, so the SSR HTML is content-identical — the boundary only adds
            // <!--$--> comment markers and changes CLIENT hydration order/priority.
            // Expected effect (mechanism-level; confirm on the Next 15 build): React
            // 18 hydrates each dehydrated boundary in its own yielding slice and
            // prioritizes a boundary the user interacts with.
            // Threshold note: `>= aboveTheFoldCount` defers from the 3rd non-spacer
            // section on, matching the eager-images rule below (`< aboveTheFoldCount`)
            // rather than the content-visibility rule (`>`), which skips one section.
            // effectiveComponentType (bound once at the map lookup above) is
            // the remapped render type — OVERWRITER and the CCT->V2
            // query-param remap included — so the eager check evaluates what
            // actually renders, never the literal wrapper type.
            const deferHydration =
              nonSpacerCount >= aboveTheFoldCount &&
              !HYDRATION_EAGER_TYPES.has(effectiveComponentType);

            const sectionEl = (
              <section
                key={'component'+index}
                id={uniqueIdForVerticalSpacer || ('component'+index)}
                data-section={uniqueDataSectionForVerticalSpacer}
                style={{
                  ...(
                    // effectiveComponentType (not component.type) so an OVERWRITER
                    // remapping to an exempt type is exempted here exactly like it
                    // is in the defer check above — the two rules must not diverge.
                    nonSpacerCount > aboveTheFoldCount &&
                    !CONTENT_VISIBILITY_EXEMPT.has(effectiveComponentType) && {
                      contentVisibility: effectiveComponentType === WnCComponentNames.FLOATINGWIDGETV2 ? 'inherit' : 'auto'
                  })
                }}>
                <DtvBox
                  sx={{
                    ...(!newComponentProps.spacer && {
                      marginTop:0,
                      marginBottom:0,
                      marginLeft:0,
                      marginRight:0,
                      ...(newComponentProps?.parentSpacers && {
                        marginTop: {
                          xs: newComponentProps?.parentSpacers?.mobileSpace || "20px",
                          sm: newComponentProps?.parentSpacers?.tabletSpace || "32px",
                          md: newComponentProps?.parentSpacers?.mediumSpace || "100px",
                          lg: newComponentProps?.parentSpacers?.largerSpace || "140px",
                          xl: newComponentProps?.parentSpacers?.extraLargeSpace || "180px",
                        }
                      }),
                    }),
                    ...(newComponentProps.spacer && {
                      margin: {
                        xs: newComponentProps.spacer !== "" && spacer[newComponentProps.spacer]
                          ? spacer[newComponentProps.spacer].mobile
                          : "0px",
                        md: newComponentProps.spacer !== "" && spacer[newComponentProps.spacer]
                          ? spacer[newComponentProps.spacer].medium
                          : "0px",
                      }
                    }),
                    ...(newComponentProps?.disableSticky!== undefined && !newComponentProps?.disableSticky && {
                      position: {
                        xs:"sticky",
                        md:"inherit"
                      },
                      zIndex:{
                        xs:1100,
                        md:"auto"
                      },
                      top:{
                        xs:0,
                        md:'auto'
                      },
                    }),
                  }}
                  {...(props.cms_type === "sb" && component.editableProps)}
                >
                  <WnCComponent
                    key={index}
                    aboveTheFold={
                      nonSpacerCount < aboveTheFoldCount &&
                      component.type !== "VERTICALSPACER"
                    }
                    lazyLoadImages={ !(
                      nonSpacerCount < aboveTheFoldCount &&
                      component.type !== "VERTICALSPACER"
                    )}
                    {...newComponentProps}
                    //TODO : To be removed
                    {
                      ...(props?.env !== "prod" && props?.reqQueryParams?.["pkg-orientation"] && {
                        orientation: props?.reqQueryParams?.["pkg-orientation"]
                      })
                    }
                  />
                </DtvBox>
              </section>
            );

            // fallback={null} is safe ONLY while no deferred child ever suspends
            // after hydration: every child here is a next/dynamic loadable (no
            // throw-a-promise path), and any React.lazy usage elsewhere under
            // src/components must ship its own Suspense boundary — re-grep on
            // change rather than trusting a count here. If a child ever adopts
            // React.lazy or use() without one, a late suspension would blank
            // the section to null, and a server-side suspension would drop it
            // from SSR HTML (SEO loss). The spec suite pins this module at
            // source level: WnCMainCanvas.tsx must contain no raw React.lazy
            // call.
            // (sectionEl keeps its own key for the eager branch; nested inside the
            // boundary below, that inner key is inert.)
            return deferHydration ? (
              <React.Suspense key={'component'+index} fallback={null}>
                {sectionEl}
              </React.Suspense>
            ) : sectionEl;
          }
        }
      } catch (error) {
        return (
          // <div role="alert"><p style={{color: '#FFFFFF', fontWeight: '500', margin: '24px', backgroundColor: 'rgba(0,0,0,0.8)'}}>Page error: Some content not available. Please try again later.</p></div>
          <ErrorDisplay error={error} component="WncMainCanvas"/>
        );
      }
      return null;
    });

    returnComponents = [
      <React.Fragment key={`contentTop`}>{builtWnCComponents}</React.Fragment>,
    ];

    return returnComponents;
  };
}

// Generated by Copilot- Start - 2026-04-01 20:31:38
/**
 * Functional wrapper that reads sbPreviewAllowed from the optimized
 * ServerDataContext and passes it as a prop to the class component.
 * Consumers should import this wrapper instead of WnCMainCanvas directly.
 */
export default function WnCMainCanvasWrapper(props: MainCanvasProps) {
  const sbConfigs = useStoryblokConfigs();
  return <WnCMainCanvas {...props} sbPreviewAllowed={sbConfigs?.sbPreviewAllowed} />;
}
// Generated by Copilot- End - 2026-04-01 20:31:38
